import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, Search, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';

const KINDS = [['all', 'All legacy'], ['imported', 'Imported from Access'], ['pre_go_live', 'Created before go-live']];
const LIMIT = 50;

const GoLiveSetting = ({ api, value, onSaved }) => {
  const [d, setD] = useState(value);
  useEffect(() => setD(value), [value]);
  const save = async () => {
    try { await api.put('/settings/go-live', { date: d }); toast.success(d ? `Go-live date set to ${d}` : 'Go-live date cleared'); onSaved(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Could not save'); }
  };
  return (
    <div className="flex items-center gap-2 text-xs text-slate-600" data-testid="go-live-setting">
      Go-live date
      <input type="date" data-testid="go-live-date-input" value={d} onChange={(e) => setD(e.target.value)} className="border border-[#E2E2DF] rounded px-2 py-1" />
      <button data-testid="go-live-save-btn" onClick={save} className="px-3 py-1 rounded-md bg-[#0F4F1B] text-white font-semibold hover:bg-[#0a3c14]">Save</button>
    </div>
  );
};

const Row = ({ r, onDone, api }) => {
  const [busy, setBusy] = useState(false);
  const mark = async () => {
    setBusy(true);
    try { await api.post(`/appraisals/${r.id}/legacy-evaluated`, { evaluated: true }); onDone(r.id); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Could not save'); setBusy(false); }
  };
  return (
    <tr className="border-t border-slate-100 hover:bg-slate-50" data-testid={`legacy-row-${r.id}`}>
      <td className="px-3 py-2 font-mono text-xs"><Link to={`/appraisals/${r.id}`} className="text-[#0F4F1B] hover:underline" data-testid={`legacy-open-${r.id}`}>A{r.appraisal_no}</Link></td>
      <td className="px-3 py-2 text-sm">{r.client || '—'}</td>
      <td className="px-3 py-2 text-xs text-slate-600">{r.address}{r.suburb ? `, ${r.suburb}` : ''}</td>
      <td className="px-3 py-2 text-xs">{r.status}</td>
      <td className="px-3 py-2 text-[11px] text-slate-500">{r.reason}</td>
      <td className="px-3 py-2 text-right">
        <button data-testid={`legacy-mark-${r.id}`} disabled={busy} onClick={mark} className="inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1 rounded-md border border-[#0F4F1B] text-[#0F4F1B] hover:bg-[#0F4F1B] hover:text-white disabled:opacity-50">
          {busy ? <Loader2 size={11} className="animate-spin" /> : <CheckCircle2 size={11} />} Evaluated
        </button>
      </td>
    </tr>
  );
};

export default function LegacyEvaluationPage() {
  const { api } = useAuth();
  const [kind, setKind] = useState('all');
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const [d, setD] = useState({ items: [], total: 0, go_live: '' });
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get('/legacy-evaluation', { params: { kind, q, page, limit: LIMIT } }); setD(data); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Could not load'); }
    finally { setLoading(false); }
  }, [api, kind, q, page]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const pages = Math.max(1, Math.ceil(d.total / LIMIT));
  const done = (id) => setD((x) => ({ ...x, items: x.items.filter((r) => r.id !== id), total: x.total - 1 }));

  return (
    <div className="space-y-4 max-w-6xl" data-testid="legacy-evaluation-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight text-slate-900 font-[Manrope]">Legacy · Needs evaluation</h1>
          <p className="text-sm text-slate-500 mt-1">Jobs imported from the old Access database or created before go-live. Open each one, check where it really sits, then mark it evaluated.</p>
        </div>
        <GoLiveSetting api={api} value={d.go_live} onSaved={load} />
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {KINDS.map(([k, l]) => (
          <button key={k} data-testid={`legacy-kind-${k}`} onClick={() => { setKind(k); setPage(1); }} className={`text-xs px-3 py-1.5 rounded-md border ${kind === k ? 'bg-[#D35400] text-white border-[#D35400]' : 'border-[#E2E2DF] text-slate-600 hover:border-[#D35400]'}`}>{l}</button>
        ))}
        <div className="relative ml-auto">
          <Search size={13} className="absolute left-2.5 top-2.5 text-slate-400" />
          <input data-testid="legacy-search-input" value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} placeholder="A-number, client or street" className="pl-8 pr-3 py-1.5 text-sm border border-[#E2E2DF] rounded-md w-64" />
        </div>
      </div>
      <div className="bg-white border border-[#E2E2DF] rounded-md overflow-x-auto">
        <div className="px-3 py-2 text-xs text-slate-500 flex items-center gap-2" data-testid="legacy-total">{loading && <Loader2 size={12} className="animate-spin" />}{d.total} job(s) still to evaluate</div>
        <table className="w-full text-left">
          <thead><tr className="text-[10px] uppercase tracking-wider text-slate-400"><th className="px-3 py-2">Job</th><th className="px-3 py-2">Client</th><th className="px-3 py-2">Address</th><th className="px-3 py-2">Old status</th><th className="px-3 py-2">Why legacy</th><th /></tr></thead>
          <tbody>{d.items.map((r) => <Row key={r.id} r={r} api={api} onDone={done} />)}</tbody>
        </table>
        {!d.items.length && !loading && <div className="p-6 text-center text-sm text-slate-400" data-testid="legacy-empty">Nothing left to evaluate here.</div>}
      </div>
      <div className="flex items-center justify-end gap-2 text-xs" data-testid="legacy-pager">
        <button data-testid="legacy-prev-btn" disabled={page <= 1} onClick={() => setPage(page - 1)} className="px-3 py-1 rounded border border-[#E2E2DF] disabled:opacity-40">Prev</button>
        <span>Page {page} of {pages}</span>
        <button data-testid="legacy-next-btn" disabled={page >= pages} onClick={() => setPage(page + 1)} className="px-3 py-1 rounded border border-[#E2E2DF] disabled:opacity-40">Next</button>
      </div>
    </div>
  );
}
